const BACKGROUND_KEY_HINTS = [
  "pendingCount",
  "unresolvedPaymentReconciliationCount",
  "health",
  "badge",
  "unread",
  "analytics",
  "recordPageView",
  "cleoLauncher",
  "listVisibleCleo",
  "authoritativeMorningDigest",
];

export type QueryLike = {
  queryKey: unknown;
  queryHash?: string;
  meta?: { background?: boolean; foreground?: boolean; pageLoad?: boolean };
  options?: {
    refetchInterval?: unknown;
    meta?: { background?: boolean; foreground?: boolean; pageLoad?: boolean };
  };
  state: {
    data?: unknown;
    error?: unknown;
    fetchStatus: string;
    status: string;
  };
  observers?: unknown[];
  getObserversCount?: () => number;
};

function queryMeta(query: QueryLike) {
  return query.options?.meta ?? query.meta;
}

function keyText(query: QueryLike): string {
  try {
    return JSON.stringify(query.queryKey ?? query.queryHash ?? "");
  } catch {
    return String(query.queryHash ?? "");
  }
}

export function isBackgroundQuery(query: QueryLike): boolean {
  const meta = queryMeta(query);
  if (meta?.background) return true;
  if (meta?.foreground || meta?.pageLoad) return false;
  if (query.options?.refetchInterval) return true;
  const text = keyText(query).toLowerCase();
  return BACKGROUND_KEY_HINTS.some((hint) => text.includes(hint.toLowerCase()));
}

export function observerCount(query: QueryLike): number {
  if (typeof query.getObserversCount === "function") {
    return query.getObserversCount();
  }
  const rec = query as Record<string, unknown>;
  for (const key of Object.keys(rec)) {
    if (key.toLowerCase() === "getobserverscount" && typeof rec[key] === "function") {
      return (rec[key] as () => number)();
    }
  }
  return Array.isArray(query.observers) ? query.observers.length : 0;
}

/**
 * Observed fetching queries drive the global bar unless they are background.
 * Cached refetches are foreground only when explicitly tagged foreground/pageLoad.
 */
export function isForegroundFetching(query: QueryLike): boolean {
  if (observerCount(query) <= 0) return false;
  if (query.state.fetchStatus !== "fetching") return false;
  if (isBackgroundQuery(query)) return false;
  const meta = queryMeta(query);
  if (meta?.foreground || meta?.pageLoad) return true;
  if (query.state.data !== undefined) return false;
  return true;
}

export function isForegroundPageFailure(query: QueryLike): boolean {
  if (observerCount(query) <= 0) return false;
  if (isBackgroundQuery(query)) return false;
  if (query.state.status !== "error") return false;
  if (query.state.data !== undefined) return false;
  return true;
}

export function queryOperationName(query: QueryLike): string {
  const key = query.queryKey;
  if (Array.isArray(key) && Array.isArray(key[0])) {
    return key[0].filter((part) => typeof part === "string").join(".").slice(0, 128) || "query";
  }
  if (Array.isArray(key)) {
    return key.filter((part) => typeof part === "string").join(".").slice(0, 128) || "query";
  }
  return "query";
}
